import { isErrorCode, type ErrorCode } from '@tekansa/core/error-codes';

/** Friendly text for every API error code. Missing a code is a compile error. */
const MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: 'Please check the highlighted fields.',
  BAD_REQUEST: 'Something about that request was not right. Please try again.',
  UNAUTHENTICATED: 'Please log in to continue.',
  INVALID_CREDENTIALS: 'Email or password is incorrect.',
  INVALID_CURRENT_PASSWORD: 'Your current password is incorrect.',
  INVALID_API_KEY: 'That API key is not valid.',
  INVALID_TOKEN: 'This link is invalid or has expired. Please request a new one.',
  EMAIL_NOT_VERIFIED: 'Please verify your email first. Check your inbox for the link we sent.',
  USER_DISABLED: 'Your account has been disabled. Contact your administrator.',
  TENANT_SUSPENDED: 'This company account is suspended. Contact Tekansa support.',
  SIGNUP_DISABLED: 'Sign-up is currently closed. Ask your team for an invite.',
  WEAK_PASSWORD: 'Please choose a stronger password.',
  CSRF_FAILED: 'Your session needs a refresh. Please reload the page and try again.',
  FORBIDDEN: 'Your role does not allow this action.',
  SESSION_REQUIRED: 'This action needs a logged-in user.',
  NO_ACTIVE_TENANT: 'You are not a member of an active company.',
  NOT_A_MEMBER: 'You are not a member of that company.',
  LAST_OWNER: 'Every company needs at least one owner. Make someone else an owner first.',
  ALREADY_MEMBER: 'This person is already a member of your team.',
  INVITE_EMAIL_MISMATCH:
    'This invite was sent to a different email address. Log in with that address to accept it.',
  ACCOUNT_EXISTS: 'You already have an account. Please log in to accept the invite.',
  NOT_FOUND: 'We could not find that. It may have been removed.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  DOMAIN_ALREADY_EXISTS: 'That domain is already added to your company.',
  MAILBOX_ALREADY_EXISTS: 'That mailbox is already added to your company.',
  DOMAIN_NOT_IN_WORKSPACE: 'Add this email address’s domain under Domains in this workspace first.',
  LIMIT_TOO_HIGH: 'One of the limits is too high.',
  HOST_NOT_ALLOWED: 'That server cannot be used here.',
  INSECURE_CONNECTION_NOT_ALLOWED:
    'Choose SSL/TLS or STARTTLS: unencrypted connections are not allowed.',
  CHECK_IN_PROGRESS: 'A check is already running. Try again in a moment.',
  TEST_EMAIL_FAILED: 'The test email could not be sent.',
  FILE_REJECTED: 'This file cannot be imported.',
  IMPORT_ALREADY_STARTED: 'This import has already started.',
  LEAD_ALREADY_EXISTS: 'A lead with this email already exists in this workspace.',
  LIST_ALREADY_EXISTS: 'A list with this name already exists.',
  NOTHING_TO_VERIFY: 'All these leads were checked recently. Nothing new to check.',
  SUPPRESSION_ALREADY_EXISTS: 'This is already on the suppression list.',
  UNDO_EXPIRED: 'This can no longer be undone.',
  CAMPAIGN_NOT_EDITABLE: 'This campaign cannot be changed right now. Pause it first.',
  CONFIRM_REQUIRED: 'Please confirm first.',
  SEND_NOT_UNKNOWN: 'Someone already decided about this email.',
  OAUTH_NOT_CONFIGURED: 'Sign-in with this provider is not set up yet.',
  MAILBOX_LIMIT_REACHED: 'This mailbox reached its sending limit. Try again later.',
  REPLY_FROM_INBOX_OFF: 'Replying from the Inbox is switched off for this workspace.',
  NOT_DRY_RUN: 'Simulated replies only work while dry run is on.',
  PLATFORM_ADMIN_REQUIRED: 'Only Tekansa platform admins can do this.',
  LINK_BLOCKLISTED:
    'A link in your emails points to a blocklisted domain. Remove it before launching.',
  TRACKING_DOMAIN_REQUIRED:
    'Tracking needs your own sending domain with a verified tracking domain (not possible with gmail.com or outlook.com addresses).',
  NO_SEED_INBOXES:
    'No seed inbox can take a test right now. Add seed inboxes or try again tomorrow.',
  SEED_ALREADY_EXISTS: 'This seed inbox is already added.',
  DRY_RUN_ON: 'Placement tests send real emails to seed inboxes: turn dry run off first.',
  ACT_AS_READ_ONLY: 'You are viewing as this person: nothing can be changed.',
  ACT_AS_ENDED: 'The time to view as this person has ended.',
  REPORT_PASSWORD_REQUIRED: 'This report needs a password.',
  REPORT_PASSWORD_WRONG: 'That password is not right.',
  REPORT_NOT_ALLOWED: 'You cannot share or schedule this report.',
  ORGANISATION_NOT_ACTIVE: 'This organisation is not active.',
  ORGANISATION_NOT_SUSPENDED: 'This organisation is not suspended.',
  STAFF_ACCOUNT: 'Not possible for a Tekansa staff account.',
  NOT_YOURSELF: 'You cannot do this to your own account.',
  EMAIL_TAKEN: 'Another account already uses this email.',
  CAMPAIGN_BUSY: 'This campaign is busy right now. Try again in a minute.',
  MEMBER_OF_ORGANISATION:
    'This person is in an organisation. Tekansa staff need their own login: use an email that is in no organisation.',
  PREVIEW_READ_ONLY: 'You are previewing a role: nothing can be changed. Exit the preview first.',
  ROLE_IN_USE: 'This role is still given to someone. Give them another role first.',
  ROLE_NAME_TAKEN: 'A role with this name already exists.',
  TEAM_NAME_TAKEN: 'A team with this name already exists.',
  INVALID_OWNER:
    'That person cannot own these records (not an active member here, or outside your team).',
  MEMBER_DEACTIVATED: 'This person is deactivated in this company.',
  CAMPAIGN_NOT_READY: 'The campaign is not ready to launch yet.',
  DOMAIN_HAS_MAILBOXES: 'Remove this domain’s mailboxes first.',
  INTERNAL: 'Something went wrong on our side. Please try again.',
};

/** Codes for problems that never reached the API. */
export type ClientErrorCode = 'NETWORK' | 'UNKNOWN';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | ClientErrorCode,
    /** Message from the server (used where it is more specific, e.g. WEAK_PASSWORD). */
    readonly serverMessage: string | undefined,
    /** Seconds from Retry-After (429). */
    readonly retryAfter?: number,
  ) {
    super(serverMessage ?? code);
    this.name = 'ApiError';
  }

  /** What to show the user. */
  get userMessage(): string {
    return messageFor(this);
  }
}

export function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) return MESSAGES.INTERNAL;
  switch (error.code) {
    case 'NETWORK':
      return 'Cannot reach Tekansa. Check your connection and try again.';
    case 'UNKNOWN':
      return MESSAGES.INTERNAL;
    case 'WEAK_PASSWORD':
    case 'LIMIT_TOO_HIGH':
    case 'DOMAIN_NOT_IN_WORKSPACE':
    case 'DOMAIN_HAS_MAILBOXES':
    case 'DOMAIN_ALREADY_EXISTS':
    case 'MAILBOX_ALREADY_EXISTS':
    case 'TEST_EMAIL_FAILED':
    case 'FILE_REJECTED':
    case 'LEAD_ALREADY_EXISTS':
    case 'LIST_ALREADY_EXISTS':
    case 'LINK_BLOCKLISTED':
    case 'TRACKING_DOMAIN_REQUIRED':
    case 'MAILBOX_LIMIT_REACHED':
    case 'NO_SEED_INBOXES':
    case 'STAFF_ACCOUNT':
    case 'NOT_YOURSELF':
    case 'CAMPAIGN_BUSY':
      // The server's message is more specific here (which limit, which domain...).
      return error.serverMessage ?? MESSAGES[error.code];
    case 'RATE_LIMITED': {
      const minutes = error.retryAfter ? Math.ceil(error.retryAfter / 60) : undefined;
      return minutes
        ? `Too many attempts. Please try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
        : MESSAGES.RATE_LIMITED;
    }
    default:
      return MESSAGES[error.code];
  }
}

/** Builds an ApiError from a failed response and its parsed body. */
export function toApiError(response: Response, body: unknown): ApiError {
  const err =
    typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'object'
      ? (body.error as { code?: unknown; message?: unknown })
      : undefined;
  const code = isErrorCode(err?.code) ? err.code : 'UNKNOWN';
  const message = typeof err?.message === 'string' ? err.message : undefined;
  const retry = Number(response.headers.get('retry-after'));
  return new ApiError(
    response.status,
    code,
    message,
    Number.isFinite(retry) && retry > 0 ? retry : undefined,
  );
}

export const errorCodeOf = (error: unknown): ApiError['code'] | undefined =>
  error instanceof ApiError ? error.code : undefined;
